import type { Accept, FileRejection } from "react-dropzone";

/**
 * ファイル種別（accept）判定の共通ユーティリティ。
 *
 * `File.type` は OS 依存で、Windows の Chrome はレジストリの
 * `HKEY_CLASSES_ROOT\.<ext>\Content Type` を参照する。この値が無い / 別アプリに
 * 上書きされている端末では .wav や .aif の MIME が空文字や
 * application/octet-stream になり、`audio/*` だけで判定すると同じファイルが
 * 端末によって弾かれる。拡張子からの推定をフォールバックに挟んで環境差を吸収する。
 */

/** 拡張子 → MIME タイプ。`File.type` が空・汎用値のときの推定に使う。 */
const EXTENSION_MIME_MAP: Record<string, string> = {
  // audio
  wav: "audio/wav",
  wave: "audio/wav",
  aif: "audio/aiff",
  aiff: "audio/aiff",
  aifc: "audio/aiff",
  mp3: "audio/mpeg",
  m4a: "audio/mp4",
  aac: "audio/aac",
  flac: "audio/flac",
  ogg: "audio/ogg",
  oga: "audio/ogg",
  opus: "audio/opus",
  wma: "audio/x-ms-wma",
  caf: "audio/x-caf",
  amr: "audio/amr",
  mid: "audio/midi",
  midi: "audio/midi",
  // video
  mp4: "video/mp4",
  m4v: "video/x-m4v",
  mov: "video/quicktime",
  webm: "video/webm",
  avi: "video/x-msvideo",
  mkv: "video/x-matroska",
  wmv: "video/x-ms-wmv",
  flv: "video/x-flv",
  mpg: "video/mpeg",
  mpeg: "video/mpeg",
  mts: "video/mp2t",
  m2ts: "video/mp2t",
  // image
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  gif: "image/gif",
  webp: "image/webp",
  svg: "image/svg+xml",
  bmp: "image/bmp",
  tif: "image/tiff",
  tiff: "image/tiff",
  heic: "image/heic",
  heif: "image/heif",
  avif: "image/avif",
  // document
  pdf: "application/pdf",
  csv: "text/csv",
  tsv: "text/tab-separated-values",
  txt: "text/plain",
  md: "text/markdown",
};

/** 拡張子を推定に使ってよい汎用 MIME（OS が種別を特定できなかったときの値）。 */
const GENERIC_MIME_TYPES = new Set([
  "application/octet-stream",
  "binary/octet-stream",
]);

/** ファイル名から小文字の拡張子（ドットなし）を取り出す。 */
const getExtension = (fileName: string): string =>
  fileName.split(".").pop()?.toLowerCase() ?? "";

/**
 * `File.type` が空・汎用値のとき、拡張子から MIME タイプを推定して返す。
 * 推定できない場合は `File.type` をそのまま返す。
 */
export const getEffectiveMimeType = (file: File): string => {
  const declared = (file.type || "").toLowerCase();
  if (declared && !GENERIC_MIME_TYPES.has(declared)) return declared;
  return EXTENSION_MIME_MAP[getExtension(file.name)] ?? declared;
};

/**
 * accept リスト（`["audio/*", ".rpp", "application/pdf"]` 形式）にマッチするか。
 *
 * MIME は宣言値と拡張子からの推定値の両方で判定する。どちらかが通れば許可し、
 * 端末ごとの MIME の揺れでファイルが黙って落ちないようにする。
 */
export const isFileTypeAccepted = (file: File, accept?: string[]): boolean => {
  if (!accept || accept.length === 0) return true;

  const fileName = file.name.toLowerCase();
  const mimeTypes = [file.type, getEffectiveMimeType(file)]
    .map((type) => (type || "").toLowerCase())
    .filter(Boolean);

  return accept.some((pattern) => {
    const p = pattern.trim().toLowerCase();
    if (!p) return false;

    // 拡張子パターン（例: .pdf, .rpp）
    if (p.startsWith(".")) return fileName.endsWith(p);

    // ワイルドカードパターン（例: image/*, audio/*）
    if (p.endsWith("/*")) {
      const category = p.slice(0, -2);
      return mimeTypes.some((type) => type.startsWith(`${category}/`));
    }

    // 完全一致（例: application/pdf）
    return mimeTypes.includes(p);
  });
};

/**
 * react-dropzone の `accept` に既知の拡張子を補う。
 *
 * react-dropzone は `{"audio/*": []}` を `File.type` だけで判定するため、MIME が
 * 空になる端末でファイルが黙って rejected に回る。ワイルドカード指定に対応する
 * 拡張子を並記して `isFileTypeAccepted` と同じ判定に揃える。
 */
export const withExtensionFallback = (accept: Accept): Accept => {
  const result: Accept = {};

  Object.entries(accept).forEach(([mimeType, extensions]) => {
    const extra: string[] = [];

    if (mimeType.endsWith("/*")) {
      const category = mimeType.slice(0, -2);
      Object.entries(EXTENSION_MIME_MAP).forEach(([ext, mapped]) => {
        if (mapped.startsWith(`${category}/`)) extra.push(`.${ext}`);
      });
    } else {
      Object.entries(EXTENSION_MIME_MAP).forEach(([ext, mapped]) => {
        if (mapped === mimeType) extra.push(`.${ext}`);
      });
    }

    result[mimeType] = Array.from(new Set([...extensions, ...extra]));
  });

  return result;
};

/** react-dropzone が弾いたファイルの理由を、ユーザー向けの 1 行にする。 */
export const formatDropRejection = (rejection: FileRejection): string => {
  const reason = rejection.errors
    .map((error) => {
      switch (error.code) {
        case "file-invalid-type":
          return "対応していない形式です";
        case "file-too-large":
          return "ファイルサイズが大きすぎます";
        case "file-too-small":
          return "ファイルサイズが小さすぎます";
        case "too-many-files":
          return "一度に追加できる件数を超えています";
        default:
          return error.message;
      }
    })
    .join(" / ");

  return `${rejection.file.name}: ${reason}`;
};
